// MSG91 OTP Widget loader. The SDK's initSendOTP() is asynchronous: it mounts an
// <msg91-otp-provider> element which fetches widget data from MSG91 and only THEN
// defines window.sendOtp / retryOtp / verifyOtp (exposeMethods). So "script loaded"
// ≠ "methods ready" — we must wait for the methods to appear.
export const MSG91_SCRIPT = "https://verify.msg91.com/otp-provider.js";
export const CAPTCHA_CONTAINER_ID = "qr-msg91-captcha";
const SDK_METHODS = ["sendOtp", "retryOtp", "verifyOtp", "getWidgetData"];
const READY_TIMEOUT_MS = 20000;
const CALL_TIMEOUT_MS = 30000;
const POLL_MS = 100;

let readyPromise = null;

export const sdkMethodsReady = () => SDK_METHODS.every((m) => typeof window[m] === "function");
// The SDK exposes the window methods BEFORE its widget-config fetch resolves; only when
// getWidgetData() returns data is the widget (and its captcha) actually usable.
export const sdkReady = () => { try { return sdkMethodsReady() && !!window.getWidgetData(); } catch { return false; } };

const waitForMethods = (state) => new Promise((resolve, reject) => {
  const deadline = Date.now() + READY_TIMEOUT_MS;
  const tick = () => {
    if (sdkReady()) return resolve(true);
    if (state.error) return reject(state.error);
    if (Date.now() > deadline) return reject(new Error("widget_init_timeout"));
    setTimeout(tick, POLL_MS);
  };
  tick();
});

export const loadWidget = (cfg) => {
  if (readyPromise) return readyPromise;
  if (sdkReady()) { readyPromise = Promise.resolve(true); return readyPromise; }
  readyPromise = new Promise((resolve, reject) => {
    const state = { error: null };
    const boot = () => {
      if (typeof window.initSendOTP !== "function") { reject(new Error("widget_load_failed")); return; }
      if (!document.getElementById(CAPTCHA_CONTAINER_ID)) { reject(new Error("widget_captcha_container_missing")); return; }
      try {
        window.initSendOTP({
          widgetId: cfg.widget_id, tokenAuth: cfg.token_auth, exposeMethods: true,
          captchaRenderId: CAPTCHA_CONTAINER_ID,
          success: () => {},
          failure: (e) => { if (!sdkReady()) state.error = new Error(widgetErrorText(e, "widget_init_failed")); },
        });
      } catch (e) { reject(e); return; }
      waitForMethods(state).then(resolve, reject);
    };
    if (typeof window.initSendOTP === "function") { boot(); return; }
    const existing = document.querySelector(`script[src="${MSG91_SCRIPT}"]`);
    const sc = existing || document.createElement("script");
    const onError = () => reject(new Error("widget_load_failed"));
    sc.addEventListener("load", boot, { once: true });
    sc.addEventListener("error", onError, { once: true });
    if (!existing) { sc.src = MSG91_SCRIPT; sc.async = true; document.head.appendChild(sc); }
  }).catch((e) => { readyPromise = null; throw e; });
  return readyPromise;
};

// With a visible captcha container the SDK silently ignores sendOtp() until the
// captcha is solved (no callback fires) — check up front instead of hanging.
export const captchaPending = () => {
  const data = typeof window.getWidgetData === "function" ? window.getWidgetData() : null;
  if (!data?.captchaValidations) return false;
  if (Number(data?.widgetMeta?.captcha_type) === 2) return false; // invisible reCAPTCHA enterprise, SDK-handled
  return typeof window.isCaptchaVerified === "function" ? !window.isCaptchaVerified() : false;
};

const withTimeout = (run) => new Promise((resolve, reject) => {
  const t = setTimeout(() => reject(new Error("widget_call_timeout")), CALL_TIMEOUT_MS);
  run((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
});

export const sdkSendOtp = (identifier) => {
  if (captchaPending()) return Promise.reject(new Error("captcha_required"));
  return withTimeout((ok, fail) => window.sendOtp(identifier, ok, fail));
};
export const sdkRetryOtp = () => withTimeout((ok, fail) => window.retryOtp(null, ok, fail));
export const sdkVerifyOtp = (code) => withTimeout((ok, fail) => window.verifyOtp(code, ok, fail));

export const widgetErrorText = (e, fallback) => {
  const raw = e && (e.message || e.msg || e.error || e.errors || (typeof e === "string" ? e : ""));
  const m = Array.isArray(raw) ? raw.filter(Boolean).join(" ") : (typeof raw === "string" ? raw : "");
  if (/captcha/i.test(m)) return "Please complete the captcha first.";
  if (/invalid widget|invalid token|unable to load widget configuration|widget_init_failed/i.test(m)) return "OTP service is misconfigured. Please ask staff for help.";
  if (/widget_load_failed|widget_init_timeout|widget_captcha_container_missing/.test(m)) return "OTP service couldn't be loaded. Check your connection and try again, or ask staff.";
  if (/widget_call_timeout/.test(m)) return "OTP service didn't respond. Please try again.";
  return m || fallback;
};

export const widgetAccessToken = (data) => (typeof data === "string" ? data : data?.message || data?.accessToken || data?.["access-token"] || data?.token || "");

export const __resetWidgetForTests = () => { readyPromise = null; };
